import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Coins,
  HandCoins,
  Landmark,
  MapPin,
  Plane,
  ScrollText,
  Wallet,
} from "lucide-react";
import Header from "../components/Header";
import { api, brl, brlPrecise, dateBR, partyStyle } from "../lib/api";
import { DETAIL } from "../constants/testIds";

const BREAKDOWN_META = [
  {
    key: "salario_base",
    label: "Salário base",
    icon: Wallet,
    accent: "bg-emerald-900 text-white",
    color: "#065F46",
  },
  {
    key: "beneficios",
    label: "Benefícios",
    icon: HandCoins,
    accent: "bg-amber-600 text-white",
    color: "#B45309",
  },
  {
    key: "auxilios",
    label: "Auxílios",
    icon: Coins,
    accent: "bg-sky-700 text-white",
    color: "#0369A1",
  },
  {
    key: "diarias",
    label: "Diárias",
    icon: Plane,
    accent: "bg-slate-800 text-white",
    color: "#1E293B",
  },
  {
    key: "outras_verbas",
    label: "Outras verbas",
    icon: ScrollText,
    accent: "bg-rose-700 text-white",
    color: "#BE123C",
  },
];

const EMPTY_STATUS = {};

function Bar({ pct, color }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-slate-100">
      <div
        className="h-1.5 rounded-full transition-[width] duration-500"
        style={{ width: `${pct}%`, backgroundColor: color }}
      />
    </div>
  );
}

export default function DetailPage() {
  const { type, id } = useParams(); // servidor | politico
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    const url = type === "politico" ? `/politicos/${encodeURIComponent(id)}` : `/servidores/${encodeURIComponent(id)}`;
    api
      .get(url)
      .then((r) => setDoc(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Não encontrado"))
      .finally(() => setLoading(false));
  }, [type, id]);

  const breakdown = doc?.breakdown || {};
  const total = Number(breakdown.gasto_total_anual || 0);
  const style = doc?.partido ? partyStyle(doc.partido) : null;

  return (
    <div className="min-h-screen">
      <Header status={EMPTY_STATUS} refreshing={false} />

      <div
        className="mx-auto max-w-[1200px] px-6 py-8 md:px-10 md:py-12"
        data-testid={DETAIL.root}
      >
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-2 text-sm text-slate-600 transition-colors hover:text-emerald-900"
          data-testid={DETAIL.backLink}
        >
          <ArrowLeft className="h-4 w-4" /> Voltar ao ranking
        </Link>

        {loading && (
          <div className="civic-card animate-pulse p-8">
            <div className="h-6 w-64 rounded bg-slate-100" />
            <div className="mt-4 h-10 w-96 rounded bg-slate-100" />
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-28 rounded bg-slate-100" />
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="civic-card p-8 text-center text-slate-600">
            {error}
          </div>
        )}

        {doc && !loading && (
          <div className="space-y-8">
            {/* HEADER CARD */}
            <div className="civic-card overflow-hidden">
              <div className="flag-stripe border-b border-slate-200 px-6 py-4 md:px-8">
                <div className="overline text-emerald-800">
                  {type === "politico"
                    ? `${doc.tipo === "senador" ? "Senador" : "Deputado Federal"} · ano ${doc.ano}`
                    : `Servidor Federal · ano ${doc.ano}`}
                </div>
              </div>
              <div className="grid gap-6 p-6 md:grid-cols-[1fr_auto] md:p-8">
                <div className="min-w-0">
                  <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                    {doc.nome}
                  </h1>
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                    {type === "politico" && doc.partido && style && (
                      <span
                        className="party-badge border"
                        style={{
                          backgroundColor: style.bg,
                          color: style.fg,
                          borderColor: style.border,
                        }}
                      >
                        {doc.partido}
                      </span>
                    )}
                    {doc.uf && doc.uf !== "-1" && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" /> {doc.uf}
                      </span>
                    )}
                    {doc.cargo && (
                      <span className="inline-flex items-center gap-1">
                        <Briefcase className="h-3.5 w-3.5" /> {doc.cargo}
                      </span>
                    )}
                    {(doc.orgao || doc.orgao_sigla) && (
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="h-3.5 w-3.5" />
                        {doc.orgao || doc.orgao_sigla}
                      </span>
                    )}
                    {doc.tipo && type === "politico" && (
                      <span className="inline-flex items-center gap-1">
                        <Landmark className="h-3.5 w-3.5" />
                        {doc.tipo === "senador"
                          ? "Senado Federal"
                          : "Câmara dos Deputados"}
                      </span>
                    )}
                  </div>
                  {doc.email && (
                    <div className="mt-3 text-xs text-slate-500">
                      Contato oficial: {doc.email}
                    </div>
                  )}
                </div>

                <div
                  className="min-w-[220px] rounded-sm border border-emerald-900 bg-emerald-900 p-6 text-right"
                  data-testid={DETAIL.totalCard}
                >
                  <div className="overline text-amber-400/90">
                    Gasto total anual
                  </div>
                  <div className="mt-2 font-display text-4xl font-bold text-white">
                    {brl(total)}
                  </div>
                  <div className="mt-1 text-xs text-emerald-100/70">
                    {brlPrecise(total)}
                  </div>
                </div>
              </div>
            </div>

            {/* BREAKDOWN GRID */}
            <div>
              <div className="mb-4 flex items-baseline justify-between">
                <h2 className="font-display text-xl font-semibold text-slate-900">
                  Composição do gasto anual
                </h2>
                <div className="text-xs text-slate-400">
                  Última atualização: {dateBR(doc.updated_at)}
                </div>
              </div>

              <div
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                data-testid={DETAIL.breakdownCard}
              >
                {BREAKDOWN_META.map((m) => {
                  const val = Number(breakdown[m.key] || 0);
                  const pct = total > 0 ? Math.min(100, (val / total) * 100) : 0;
                  const Icon = m.icon;
                  return (
                    <div key={m.key} className="civic-card p-5">
                      <div className="flex items-start justify-between">
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-sm ${m.accent}`}
                        >
                          <Icon className="h-4 w-4" aria-hidden="true" />
                        </div>
                        <div className="overline text-slate-400">
                          {pct.toFixed(1)}%
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-slate-500">
                        {m.label}
                      </div>
                      <div className="mt-1 font-display text-2xl font-bold text-slate-900">
                        {brl(val)}
                      </div>
                      <div className="mt-3">
                        <Bar pct={pct} color={m.color} />
                      </div>
                    </div>
                  );
                })}

                <div className="civic-card border-emerald-900/30 bg-emerald-900/[0.03] p-5">
                  <div className="overline text-emerald-800">Total</div>
                  <div className="mt-2 font-display text-2xl font-bold text-emerald-900">
                    {brl(total)}
                  </div>
                  <div className="mt-3 text-xs text-slate-500">
                    Soma de todas as parcelas oficiais do ano {doc.ano}.
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-sm border border-slate-200 bg-slate-50 p-5 text-xs leading-relaxed text-slate-600">
              <b>Nota:</b> Valores extraídos das APIs oficiais (Portal da
              Transparência, Câmara dos Deputados e Senado Federal). Para{" "}
              <i>servidores federais</i>, os valores são a soma da remuneração
              bruta divulgada mensalmente. Para <i>parlamentares</i>, o valor
              exibido corresponde à cota parlamentar / verba indenizatória do
              ano — não inclui subsídio fixo.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
